import { IncomeAndExpenseCalculations } from 'app/calcs/Accountant/Transactions'
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import { calculateGrandScore, classifyTransaction } from 'app/calcs/Accountant/Transactions/predicates'
import { INcpersPriorPensionInputs } from 'app/calcs/customers/ncpers/prior'
import * as ExpenseCalculators from 'app/calcs/expenses'
import { FormOfPaymentOptions } from 'app/calcs/pensions'
import { IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { ISpousalPensionIncome, ISpousalPensionInputs } from 'app/calcs/pensions/spousal'
import { getScorePhrase } from 'app/calcs/Planners/Fidelity/index'
import { INcpersPensionInputs, INcpersRunCalculationsRequest, NcpersActions } from 'app/redux/Customers/ncpers/actions'
import { IRetirementPerson, isSamePersonForSSA, Profile, retirementDate } from 'app/redux/Profile/model'
import { IWorkbookPayoutSchedule, SSAActions } from 'app/redux/SSA/actions'
import { WorkbookServiceResponse } from 'app/services/models/Workbook'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import { RequestCache } from 'app/utils/RequestCache'
import * as _ from 'lodash'
import * as moment from 'moment'

enum InsightSeverity { // in order of severity
  high, medium, low
}
interface IInsight {
  severity: InsightSeverity
  description: string
  suggestion: string
}

interface IRetirementScoreCalculationsOutput {
  analysis: TX.ITransactionsAnalysis
  insights: IInsight[]
  score: number
  scorePhrase: string
  timeline: TX.ITransactionItem[]
}

function membersAreSame(a: IRetirementPerson[], b: IRetirementPerson[]): boolean {
  const d = _.differenceWith(a, b, isSamePersonForSSA)
  // tslint:disable-next-line:no-debugger
  //if (0 !== d.length) { debugger }
  return 0 === d.length
}

const debug = utils.makeDebug(false)

//  cached calculators
const runRetirementScoreCalculations = _.memoize(calculateRetirementScore, JSON.stringify)
const runNcpersCalculations = _.memoize(NcpersActions.runCalculations.bind(NcpersActions), JSON.stringify)
const runSSAOptimizer = _.memoize((folks: IRetirementPerson[]) => {
  return SSAActions.runOptimizer(folks)
}, JSON.stringify)
let lastFolks = ''
let lastResult
function xrunSSAOptimizer(folks: IRetirementPerson[]) {
  const newFolks = JSON.stringify(folks)
  const areSame = newFolks === lastFolks
  if (areSame) {
    // tslint:disable-next-line:no-console
    console.log('SSA inputs are same: ' + newFolks)
    return lastResult
  } else {
    // tslint:disable-next-line:no-console
    console.log('SSA inputs have changed. was: ' + lastFolks + ', is now: ' + newFolks)
    lastFolks = newFolks
    return lastResult = xrunSSAOptimizer(folks)
  }
}

function hasSomeData(scoreCalcuationOutput: IRetirementScoreCalculationsOutput) {
  if (!scoreCalcuationOutput || !scoreCalcuationOutput.analysis) { return false }
  return (0 !== scoreCalcuationOutput.analysis.averageMonthlyExpenses) || (0 !== scoreCalcuationOutput.analysis.averageMonthlyNetIncome)
}

function calculateRetirementScore(profile: Profile): Promise<IRetirementScoreCalculationsOutput> {
  // tslint:disable-next-line:no-console
  //console.log('running calculateRetirementScore')

  const firstToRetire = profile.getFirstRetirement()
  let startWhen = utils.todaysDate()
  let endWhen = startWhen
  const timedStart = moment.utc()

  // end the analysis when the last person dies
  if (firstToRetire) {
    endWhen = DateCalcs.toCalcDate(profile.getLastDeathDate())
  }

  const calculator = new IncomeAndExpenseCalculations(profile)
  return calculator.getTimeLineTransactions()
    .then((timeline) => {
      // look for the first income and set the starting time to that
      const index = _.findIndex(timeline, (tx) => {
        const z = classifyTransaction(tx)
        return !_.isUndefined(z.income) && z.income !== 0
      })
      if (index >= 0) {
        timeline = _.takeRight(timeline, timeline.length - index)
        startWhen = timeline[0].when
        // tslint:disable-next-line:no-debugger
        //debugger
      }

      return calculateGrandScore(profile, startWhen, endWhen)
        .then((analysis) => {
          const elapsed = moment.duration(moment.utc().diff(timedStart))
          const insights: IInsight[] = []

          debug('elapsed time for calculateRetirementScore calculations: ' + elapsed.asSeconds())

          if (profile.memberPIAMissing()) {
            const member = profile.getHeadOfHousehold()
            insights.push({
              severity: InsightSeverity.medium,
              description: member.firstName + ' doesn\'t have a PIA.',
              suggestion: 'click <a href="#socialsecurity/ssa_benefits">here</a> to enter one.'
            })
          }
          if (profile.spousePIAMissing()) {
            const spouse = profile.getSpouse()
            insights.push({
              severity: InsightSeverity.medium,
              description: spouse.firstName + ' doesn\'t have a PIA.',
              suggestion: 'click <a href="#socialsecurity/ssa_benefits">here</a> to enter one.'
            })
          }

          // look for too many times of a poor monthly score
          const troubled = _.filter(analysis.scores, (s) => s.score < 80)
          if (troubled.length >= 3) {
            // because we have too many troubled scores, the maximum score is limited to 65
            const adjustedScore = _.meanBy(analysis.scores, (q) => q.score > 100 ? 100 : q.score) * 0.65
            const troubledPeriods = DateCalcs.groupDates(_.map(troubled, (q) => q.when))
            insights.push({
              severity: InsightSeverity.high,
              description: 'There are ' + troubled.length + ' months of your retirement that your projected monthly balance might be too low to cover your expenses. See the periods: ' + troubledPeriods + '.',
              suggestion: 'review your pension and retirement withdrawl dates.'
            })
            analysis.score = adjustedScore
          }

          const score = utils.round(analysis.score)
          const scorePhrase = getScorePhrase(score)
          return { analysis, insights, score, scorePhrase, timeline }
        })
    })

}

/**
 * Calculate the monthly payment for the ncpers pension.
 *
 * @param request The parameters needed to perform the calculations
 *
 * @returns The IMonthlyPensionAmounts
 */
function getNcpersMonthlyPensionAmount(request: INcpersRunCalculationsRequest): Promise<IMonthlyPensionPayments> {
  return runNcpersCalculations(request)
    .then((wr) => {
      return NcpersActions.getMonthlyPensionAmount(request, wr)
    })
}

export {
  InsightSeverity,
  IRetirementScoreCalculationsOutput,

  hasSomeData,

  // ncpers pensions-related
  getNcpersMonthlyPensionAmount,
  runRetirementScoreCalculations,
  runNcpersCalculations,

  // SSA-related
  runSSAOptimizer
}



// WEBPACK FOOTER //
// ./src/app/calcs/Accountant/index.ts